import semver from 'semver';
import Vue from 'vue';
import Vuex from 'vuex';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import IntervenersService from '@/services/interveners.service';
import LZString from 'lz-string';

import {
    getProductsParams,
    getProductKeys,
    getAccountsOperationsConfigurations,
    getNetAssetValueDate
} from '../utils/netAssetValueDate';

Vue.use(Vuex);

export default new Vuex.Store({
    state: {
        accounts: [],
        // appVersion es la versión actual del build
        appVersion: null,
        // appVersionMinimal es la versión mínima que debe tener el usuario
        appVersionMinimal: null,
        documents: {},
        documentsSection: 0,
        evolutions: [],
        loadingState: {
            isLoading: false,
            message: null
        },
        globalNotifiactions: 0,
        intervener: {},
        netAssetValueDate: null,
        notificationsSection: 0,
        canOperate: true,
        OTPData: {},
        countriesOptions: [],
        videoBannerActive: false,
        intervenerBalance: {},
        selectedBankAccount: null,
        canBeTaxDown: null,
        canBeTaxDownType: null,
        hasTaxDown: null,
        hiddenTaxDownBanner: false,
        canBeCobasPlus: null,
        hasCobasPlus: null,
        redirectToCobasPlus: null,
        hiddenCobasPlusBanner: false,
        wealthReaderResults: {},
        uuid_wealthreader_operation: '',
        rolesDefinition: [],
        hasYoidEnabled: false
    },
    mutations: {
        SET_APP_VERSION(state, version) {
            state.appVersion = version;
        },
        SET_APP_VERSION_MINIMAL(state, version) {
            state.appVersionMinimal = version;
        },
        SET_LOADING_STATE(state, { isLoading, message }) {
            state.loadingState.isLoading = isLoading;
            state.loadingState.message = message;
        },
        SET_NOTIFICATIONS_SECTION_NUMBER(state, number) {
            state.notificationsSection = number;
            state.globalNotifiactions = state.documentsSection + state.notificationsSection;
        },
        SET_DOCUMENTS_SECTION_NUMBER(state, hasDocumentsSigned) {
            state.documentsSection = hasDocumentsSigned ? 0 : 1;
            state.globalNotifiactions = state.notificationsSection + state.documentsSection;
        },
        SET_INTERVENER(state, intervener) {
            state.intervener = intervener;
        },
        SET_ACCOUNTS(state, accounts) {
            state.accounts = accounts;
        },
        SET_DEFAULT_STORE(state) {
            state.globalNotifiactions = 0;
            state.documentsSection = 0;
            state.notificationsSection = 0;
            state.intervener = {};
            state.accounts = [];
            state.evolutions = [];
            state.documents = {};
            state.netAssetValueDate = null;
            // NO SETEAR A NULL las versiones de la app, porque puede romperse. En la configuración inicial de la aplicación
            // se configura la versión de la app y si después la dejamos a null, no se va a volver configurar y tendremos un error
            // al comprobar si la app está actualizada o no.
            // state.appVersion = null;
            // state.appVersionMinimal = null;
            state.documents = {};
            state.documentsSection = 0;
            state.evolutions = [];
            state.canOperate = true;
            state.OTPData = {};
            state.countriesOptions = [];
            state.videoBannerActive = false;
            state.intervenerBalance = {};
            state.selectedBankAccount = null;
            state.canBeTaxDown = null;
            state.canBeTaxDownType = null;
            state.hasTaxDown = null;
            state.hiddenTaxDownBanner = false;
            state.canBeCobasPlus = null;
            state.hasCobasPlus = null;
            state.redirectToCobasPlus = null;
            state.hiddenCobasPlusBanner = false;
            state.wealthReaderResults = {};
            state.uuid_wealthreader_operation = '';
        },
        SET_ACCOUNTS_EVOLUTIONS(state, accountsEvolutions) {
            state.evolutions = accountsEvolutions;
        },
        SET_DOCUMENTS(state, documents) {
            state.documents = documents;
        },
        RESET_DOCUMENT_ITEMS(state) {
            state.documents = {};
        },
        SET_NET_ASSET_VALUE_DATE(state, date) {
            state.netAssetValueDate = date;
        },
        SET_CAN_OPERATE(state, canOperate) {
            state.canOperate = canOperate;
        },
        SET_OTP_DATA(state, OTPData) {
            state.OTPData = OTPData;
        },
        SET_COUNTRIES_OPTIONS(state, countriesOptions) {
            state.countriesOptions = countriesOptions;
        },
        SET_VIDEOBANNER_ACTIVE(state, videoBannerActive) {
            state.videoBannerActive = videoBannerActive;
        },
        SET_INTERVERNER_BALANCE(state, intervenerBalance) {
            state.intervenerBalance = intervenerBalance;
        },
        SET_SELECTED_BANK_ACCOUNT(state, selectedBankAccount) {
            state.selectedBankAccount = selectedBankAccount;
        },
        SET_CAN_BE_TAXDOWN(state, canBeTaxDown) {
            state.canBeTaxDown = canBeTaxDown;
        },
        SET_CAN_BE_TAXDOWN_TYPE(state, canBeTaxDownType) {
            state.canBeTaxDownType = canBeTaxDownType;
        },
        SET_HAS_TAXDOWN(state, hasTaxDown) {
            state.hasTaxDown = hasTaxDown;
        },
        SET_HIDDEN_TAXDOWN_BANNER(state, hiddenTaxDownBanner) {
            state.hiddenTaxDownBanner = hiddenTaxDownBanner;
        },
        SET_CAN_BE_COBASPLUS(state, canBeCobasPlus) {
            state.canBeCobasPlus = canBeCobasPlus;
        },
        SET_HAS_COBASPLUS(state, hasCobasPlus) {
            state.hasCobasPlus = hasCobasPlus;
        },
        SET_REDIRECT_TO_COBASPLUS(state, redirectToCobasPlus) {
            state.redirectToCobasPlus = redirectToCobasPlus;
        },
        SET_HIDDEN_COBASPLUS_BANNER(state, hiddenCobasPlusBanner) {
            state.hiddenCobasPlusBanner = hiddenCobasPlusBanner;
        },
        SET_WEALTH_READER_RESULTS(state, results) {
            state.wealthReaderResults = results;
        },
        SET_UUID_WEALTHREADER_OPERATION(state, uuid_wealthreader_operation) {
            state.uuid_wealthreader_operation = uuid_wealthreader_operation;
        },

        SET_ROLES_DEFINITION(state, rolesDefinition) {
            state.rolesDefinition = rolesDefinition;
        },

        SET_HAS_YOID_ENABLED(state, hasYoidEnabled) {
            state.hasYoidEnabled = hasYoidEnabled;
        }
    },
    actions: {
        setAppVersion: async ({ commit }) => {
            if (!Capacitor.isNativePlatform()) {
                return commit('SET_APP_VERSION', null);
            }

            const { version } = await App.getInfo();
            commit('SET_APP_VERSION', version);
        },
        setAppVersionMinimal: ({ commit }, version) => {
            commit('SET_APP_VERSION_MINIMAL', version);
        },
        hideLoading: ({ commit }) => {
            commit('SET_LOADING_STATE', {
                isLoading: false,
                message: null
            });
        },
        showLoading: ({ commit }) => {
            commit('SET_LOADING_STATE', {
                isLoading: true,
                message: 'Cargando...'
            });
        },
        setNotificationsSectionNumber: ({ commit }, number) => {
            commit('SET_NOTIFICATIONS_SECTION_NUMBER', number);
        },
        setDocumentsSectionNumber: ({ commit }, hasDocumentsSigned) => {
            commit('SET_DOCUMENTS_SECTION_NUMBER', hasDocumentsSigned);
        },
        setIntervener: ({ commit }, intervener) => {
            commit('SET_INTERVENER', intervener);
        },
        setIntervenerAccounts: ({ commit }, accounts) => {
            commit('SET_ACCOUNTS', accounts);
        },
        setDefaultStore: ({ commit }, accounts) => {
            commit('SET_DEFAULT_STORE', accounts);
        },
        setAccountsEvolutions: ({ commit }, evolutions) => {
            commit('SET_ACCOUNTS_EVOLUTIONS', evolutions);
        },
        setDocuments: ({ commit }, documents) => {
            commit('SET_DOCUMENTS', documents);
        },
        resetDocumentItems: ({ commit }) => {
            commit('RESET_DOCUMENT_ITEMS');
        },
        setNetAssetValueDate: ({ commit }, date) => {
            commit('SET_NET_ASSET_VALUE_DATE', date);
        },
        fetchNetAssetValueDate: ({ commit, state }) => {
            if (state.netAssetValueDate) {
                return;
            }
            // set the date to get the net asset value
            const accountsOperationsConfigs = getAccountsOperationsConfigurations();
            const accountPromises = accountsOperationsConfigs.map(({ account, cfg }) =>
                Vue.prototype.$operationsService.getRelatedOperations(account.uuid, cfg)
            );

            Promise.all(accountPromises).then(accountsOperations => {
                const productKeys = getProductKeys(accountsOperations);
                const productParams = getProductsParams(productKeys);

                return Vue.prototype.$productsService
                    .getProducts({ params: productParams })
                    .then(response => {
                        const valueDate = getNetAssetValueDate(response);
                        commit('SET_NET_ASSET_VALUE_DATE', valueDate);
                    });
            });
        },
        setOTPData: ({ commit }, OTPData) => {
            commit('SET_OTP_DATA', OTPData);
        },
        setCanOperate: ({ commit }, canOperate) => {
            commit('SET_CAN_OPERATE', canOperate);
        },
        setCountriesOptions: ({ commit }, countriesOptions) => {
            commit('SET_COUNTRIES_OPTIONS', countriesOptions);
        },
        setVideoBannerActive: ({ commit }, videoBannerActive) => {
            commit('SET_VIDEOBANNER_ACTIVE', videoBannerActive);
        },
        setIntervenerBalance: ({ commit }, intervenerBalance) => {
            commit('SET_INTERVERNER_BALANCE', intervenerBalance);
        },
        setSelectedBankAccount: ({ commit }, selectedBankAccount) => {
            commit('SET_SELECTED_BANK_ACCOUNT', selectedBankAccount);
        },
        setCanBeTaxDown: ({ commit }, canBeTaxDown) => {
            commit('SET_CAN_BE_TAXDOWN', canBeTaxDown);
        },
        setCanBeTaxDownType: ({ commit }, canBeTaxDownType) => {
            commit('SET_CAN_BE_TAXDOWN_TYPE', canBeTaxDownType);
        },
        setHasTaxDown: ({ commit }, hasTaxDown) => {
            commit('SET_HAS_TAXDOWN', hasTaxDown);
        },
        setHiddenTaxDownBanner: ({ commit }, hiddenTaxDownBanner) => {
            commit('SET_HIDDEN_TAXDOWN_BANNER', hiddenTaxDownBanner);
        },
        setCanBeCobasPlus: ({ commit }, canBeCobasPlus) => {
            commit('SET_CAN_BE_COBASPLUS', canBeCobasPlus);
        },
        setHasCobasPlus: ({ commit }, hasCobasPlus) => {
            commit('SET_HAS_COBASPLUS', hasCobasPlus);
        },
        setRedirectToCobasPlus: ({ commit }, redirectToCobasPlus) => {
            commit('SET_REDIRECT_TO_COBASPLUS', redirectToCobasPlus);
        },
        setHiddenCobasPlusBanner: ({ commit }, hiddenCobasPlusBanner) => {
            commit('SET_HIDDEN_COBASPLUS_BANNER', hiddenCobasPlusBanner);
        },
        setWealthReaderResults: ({ commit }, results) => {
            commit('SET_WEALTH_READER_RESULTS', results);
        },
        setUuidWealthReaderOperation: ({ commit }, uuid_wealthreader_operation) => {
            commit('SET_UUID_WEALTHREADER_OPERATION', uuid_wealthreader_operation);
        },

        rolesDefinition: async ({ state, commit }) => {
            const storedRolesDefinition = state.rolesDefinition;
            if (!storedRolesDefinition || storedRolesDefinition.length <= 0) {
                const res = await IntervenersService.getRolesDefinition();
                commit('SET_ROLES_DEFINITION', res.data.roles_definition);
            }

            return state.rolesDefinition;
        },

        setHasYoidEnabled: ({ commit }, hasYoidEnabled) => {
            const hasYoidEnabledBool = hasYoidEnabled === true || hasYoidEnabled === 'true';
            commit('SET_HAS_YOID_ENABLED', hasYoidEnabledBool);
        }
    },
    modules: {},
    getters: {
        getAppVersion(state) {
            return state.appVersion;
        },
        getAppVersionMinimal(state) {
            return state.appVersionMinimal;
        },
        getIsAppVersionOutdated(state) {
            return state.appVersionMinimal && semver.ltr(state.appVersion, state.appVersionMinimal);
        },
        getLoadingState(state) {
            return state.loadingState;
        },
        getIntervener(state) {
            const isEmptyOrNotDefined = state.intervener
                ? Object.keys(state.intervener).length === 0
                : true;

            if (isEmptyOrNotDefined && localStorage.getItem('PACOUS')) {
                try {
                    const decompressed = LZString.decompressFromUTF16(
                        localStorage.getItem('PACOUS')
                    );
                    state.intervener = JSON.parse(decompressed).private_area.intervener;
                } catch (e) {
                    console.error('Error al descomprimir PACOUS:', e);
                }
            }

            return state.intervener;
        },
        getIntervenerAccounts(state) {
            if (!state.accounts && localStorage.getItem('PACOUS')) {
                try {
                    const decompressed = LZString.decompressFromUTF16(
                        localStorage.getItem('PACOUS')
                    );
                    state.accounts = JSON.parse(decompressed).private_area.accounts;
                } catch (e) {
                    console.error('Error al descomprimir PACOUS:', e);
                }
            }
            return state.accounts;
        },
        getAccountsEvolutions(state) {
            return state.evolutions;
        },
        getDocuments(state) {
            return state.documents;
        },
        getNetAssetValueDate(state) {
            return state.netAssetValueDate;
        },
        getCanOperate(state) {
            return state.canOperate;
        },
        getOTPData(state) {
            return state.OTPData;
        },
        getCountriesOptions(state) {
            return state.countriesOptions;
        },
        videoBannerActive(state) {
            return state.videoBannerActive;
        },
        intervenerBalance(state) {
            return state.intervenerBalance;
        },
        selectedBankAccount(state) {
            return state.selectedBankAccount;
        },
        canBeTaxDown(state) {
            return state.canBeTaxDown;
        },
        canBeTaxDownType(state) {
            return state.canBeTaxDownType;
        },
        hasTaxDown(state) {
            return state.hasTaxDown;
        },
        hiddenTaxDownBanner(state) {
            return state.hiddenTaxDownBanner;
        },
        canBeCobasPlus(state) {
            return state.canBeCobasPlus;
        },
        hasCobasPlus(state) {
            return state.hasCobasPlus;
        },
        redirectToCobasPlus(state) {
            return state.redirectToCobasPlus;
        },
        hiddenCobasPlusBanner(state) {
            return state.hiddenCobasPlusBanner;
        },
        getWealthReaderResults(state) {
            return state.wealthReaderResults;
        },
        getUuidWealthReaderOperation(state) {
            return state.uuid_wealthreader_operation;
        },

        getHasYoidEnabled(state) {
            return state.hasYoidEnabled;
        }
    }
});
